import { create } from 'zustand';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'offline';

interface ConnectionStoreState {
  status: ConnectionStatus;
  lastError: string | null;
  lastErrorAtMs: number | null;
  reportSuccess: () => void;
  reportError: (error: unknown) => void;
  setBrowserOnline: (online: boolean) => void;
}

const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || error.name;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }
  return 'Connection error';
};

export const useConnectionStore = create<ConnectionStoreState>((set, get) => ({
  status: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'connected',
  lastError: null,
  lastErrorAtMs: null,
  reportSuccess: () => {
    const current = get();
    // Never downgrade offline → connected when the browser reports offline.
    // The browser online listener owns that transition.
    if (current.status === 'offline' && typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }
    if (current.status !== 'connected') {
      set({ status: 'connected', lastError: null, lastErrorAtMs: null });
    }
  },
  reportError: (error: unknown) => {
    const current = get();
    if (current.status === 'offline') {
      return;
    }
    set({
      status: 'reconnecting',
      lastError: describeError(error),
      lastErrorAtMs: Date.now(),
    });
  },
  setBrowserOnline: (online: boolean) => {
    if (online) {
      set({ status: 'connected', lastError: null, lastErrorAtMs: null });
    } else {
      set({ status: 'offline', lastError: 'Your device is offline.', lastErrorAtMs: Date.now() });
    }
  },
}));

/**
 * Install browser `online`/`offline` listeners so the store reflects network
 * status immediately. Returns a disposer suitable for test teardown.
 */
export const installConnectionBrowserListeners = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const setBrowserOnline = useConnectionStore.getState().setBrowserOnline;
  const onOnline = (): void => setBrowserOnline(true);
  const onOffline = (): void => setBrowserOnline(false);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

/**
 * Build a Firestore `onSnapshot` error callback that forwards to the
 * connection store so a degraded listener surfaces as a reconnecting banner
 * in the UI. Optionally chains to a caller-supplied handler.
 */
export const createSnapshotErrorReporter = (
  onError?: (error: Error) => void,
): ((error: Error) => void) => {
  return (error: Error): void => {
    useConnectionStore.getState().reportError(error);
    onError?.(error);
  };
};

/**
 * Build a Firestore `onSnapshot` next callback that reports a healthy
 * connection to the store before forwarding to the real handler.
 */
export const wrapSnapshotSuccess = <T>(onNext: (value: T) => void): ((value: T) => void) => {
  return (value: T): void => {
    useConnectionStore.getState().reportSuccess();
    onNext(value);
  };
};
